import { useCallback, useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { log } from '@/logger/logger';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { prepareNodeHistoryCaptureData } from '@/utils/flow';
import { FF_PROMPT_NODE_HISTORY } from '@/consts/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { captureNodeHistoryApi } from '@/api/nodeHistory/nodeHistory.api';
import { invalidateNodeHistoryQueries } from '@/queries/useNodeHistory';
import type { Edge, Node } from '@/types/node';

const logger = log.getLogger('usePromptNodeHistoryBatch');

/** Aligns with core `DEFERRED_RACE_RETRY_BACKOFF_MS` — second FE history refresh after batch terminal. */
const DEFERRED_HISTORY_INVALIDATE_RETRY_MS = 2000;

/**
 * Fire-and-forget capture of prompt-node history after a batch executes (see
 * {@link prepareNodeHistoryCaptureData} for the upstream walk).
 *
 * Never throws — capture must not affect the run. On failure we log status + message only, never the
 * raw AxiosError, whose `config.data` carries the submitted prompt graph (user text) into the remote
 * log collector. On success we invalidate the workflow's history query so an open drawer refreshes.
 */
const captureNodeHistory = (
  queryClient: QueryClient,
  workflowId: string,
  batchId: string,
  modelNodes: Node[],
  preparedRunNodes: Node[],
  nodes: Node[],
  edges: Edge[],
  numberOfRuns = 1,
): void => {
  const capture = prepareNodeHistoryCaptureData(modelNodes, preparedRunNodes, nodes, edges);
  void captureNodeHistoryApi({
    workflowId,
    batchId,
    nodes: capture.nodes,
    edges: capture.edges,
    modelNodeIds: modelNodes.map((node) => node.id),
    numberOfRuns,
  })
    .then(() => {
      void invalidateNodeHistoryQueries(queryClient, workflowId);
    })
    .catch((error) => {
      if (!(error instanceof AxiosError)) {
        logger.warn('Could not capture node history: unknown error');
        return;
      }
      const status = error.response?.status;
      // Our API always answers with JSON, so a plain 403 is our own ForbiddenError (e.g. non-editor).
      // A Cloudflare WAF block instead returns an HTML block page — that source, not the 403 alone, is
      // the tell. Cross-origin, that block page carries no CORS headers, so the browser hides the
      // response entirely and axios reports an opaque ERR_NETWORK that no origin response produces.
      const contentType = String(error.response?.headers?.['content-type'] ?? '');
      const body: unknown = error.response?.data;
      const fromCloudflare =
        contentType.includes('text/html') || (typeof body === 'string' && /cloudflare/i.test(body));
      const opaqueEdgeError = !error.response && error.code === 'ERR_NETWORK';
      if ((status === 403 && fromCloudflare) || opaqueEdgeError) {
        logger.warn(`Node history capture likely blocked by Cloudflare WAF: ${status ?? error.code}`);
        return;
      }
      logger.warn(`Could not capture node history: ${`${status ?? ''} ${error.message}`.trim()}`);
    });
};

/**
 * Prompt-node history side effects for batch run: capture after execute, and invalidate on batch
 * terminal (immediate + one delayed retry for deferred enrich).
 */
export const usePromptNodeHistoryBatch = (workflowId: string) => {
  const queryClient = useQueryClient();
  // Read via the custom hook (not raw useFeatureFlagEnabled) so `?featureFlagsOn=` URL overrides
  // apply — e2e relies on this to force the flag on without depending on the PostHog project state.
  const { value: isPromptNodeHistory } = useFeatureFlag(FF_PROMPT_NODE_HISTORY);
  // Capture is authorized server-side by canEdit (editor-only), so don't fire it for non-editors —
  // those requests are guaranteed 403s. Mirrors the editor gate on the ⋯ menu (useNodeHistoryMenuItem).
  const isWorkflowEditor = useUserWorkflowRole() === 'editor';
  const shouldCapturePromptNodeHistory = isPromptNodeHistory && isWorkflowEditor;

  /**
   * Batches that already got the terminal history refresh path (immediate + one delayed retry).
   * Caps at two invalidates per batchId so deferred enrich finishing after terminal can still refresh.
   */
  const historyInvalidatedBatches = useRef<Set<string>>(new Set());
  const historyDeferredInvalidateTimers = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    const timers = historyDeferredInvalidateTimers.current;
    return () => {
      timers.forEach((timerId) => {
        clearTimeout(timerId);
      });
      timers.clear();
    };
  }, []);

  const refreshHistoryOnBatchTerminal = useCallback(
    (batchId: string) => {
      // Deferred enrich usually folds around batch terminal — refresh menu/drawer then too (capture
      // success alone often runs before any version exists). Immediate invalidate + one delayed
      // retry (≤2 per batchId) so a late enrich still refreshes after the once-per-batch guard.
      if (!shouldCapturePromptNodeHistory || historyInvalidatedBatches.current.has(batchId)) {
        return;
      }
      historyInvalidatedBatches.current.add(batchId);
      void invalidateNodeHistoryQueries(queryClient, workflowId);
      const timerId = window.setTimeout(() => {
        historyDeferredInvalidateTimers.current.delete(batchId);
        void invalidateNodeHistoryQueries(queryClient, workflowId);
      }, DEFERRED_HISTORY_INVALIDATE_RETRY_MS);
      historyDeferredInvalidateTimers.current.set(batchId, timerId);
    },
    [shouldCapturePromptNodeHistory, queryClient, workflowId],
  );

  const captureAfterBatchExecute = useCallback(
    (args: {
      batchId: string;
      modelNodes: Node[];
      preparedRunNodes: Node[];
      nodes: Node[];
      edges: Edge[];
      numberOfRuns?: number;
    }) => {
      if (!shouldCapturePromptNodeHistory || !args.modelNodes.length) return;
      captureNodeHistory(
        queryClient,
        workflowId,
        args.batchId,
        args.modelNodes,
        args.preparedRunNodes,
        args.nodes,
        args.edges,
        args.numberOfRuns,
      );
    },
    [shouldCapturePromptNodeHistory, queryClient, workflowId],
  );

  return {
    shouldCapturePromptNodeHistory,
    refreshHistoryOnBatchTerminal,
    captureAfterBatchExecute,
  };
};
